import React, { useEffect } from 'react'
import { useListState } from '@mantine/hooks'
import { toLower } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { TimelineProps } from '@node-space/ui-components'
import { PaymentsResponse, PaymentStatus, PaymentType } from 'types/payments'
import { calculatePercentageDiff } from 'utils/utils'
import { PaymentTimelineRowInfo } from '../components/Table/Timeline/PaymentTimelineRowInfo'

export const useUpdateTimeline = (transaction: PaymentsResponse) => {
  const { t } = useTranslation()
  const isPaymentIn = transaction?.type === PaymentType.IN
  const amountDue = transaction?.walletCurrency
    ? transaction.paidCurrency?.amount - transaction.paidCurrency?.actual
    : 0

  const [values, handlers] = useListState<TimelineProps['items'][number]>([
    {
      variant: 'info',
      date: new Date(transaction?.dateCreated),
      Title: (
        <PaymentTimelineRowInfo
          generalInfo={t('newPaymentInfoTimeline', {
            paymentType: isPaymentIn ? t('payments.in') : t('payments.out'),
          })}
          boldInfo={`${transaction?.displayCurrency?.amount} ${transaction?.displayCurrency?.currency}`}
          testid="timeline-action-of-value"
        />
      ),
    },
  ])

  useEffect(() => {
    const canMarkPaymentComplete = transaction?.status === PaymentStatus.PROCESSING
    const isUnderpaidStatus = transaction?.status === PaymentStatus.UNDERPAID
    const isOverpaidStatus = transaction?.status === PaymentStatus.OVERPAID

    let updatedTimelineRows: TimelineProps['items'] = [...values]

    if (
      transaction?.quoteStatus !== 'TEMPLATE' &&
      transaction?.quoteStatus !== 'PENDING' &&
      values.length === 1
    ) {
      updatedTimelineRows.push({
        variant: 'info',
        Title: (
          <PaymentTimelineRowInfo
            generalInfo={t('customerAcceptedQuoteTimeline', {
              action: isPaymentIn
                ? t('transactionAction.pay').toLowerCase()
                : t('transactionAction.receive').toLowerCase(),
            })}
            boldInfo={`${transaction?.paidCurrency?.amount} ${transaction?.paidCurrency?.currency}`}
          />
        ),
      })
    }

    if (transaction?.transactions?.length > 1 && !isUnderpaidStatus && !isOverpaidStatus) {
      transaction?.transactions?.forEach(current => {
        updatedTimelineRows.push({
          variant: 'info',
          date: new Date(current?.dateConfirmed),
          Title: (
            <PaymentTimelineRowInfo
              generalInfo={t('paymentOfTimeline', {
                action: isPaymentIn ? t('payments.received') : t('payments.sent'),
              })}
              testid="timeline-action-of-value"
              boldInfo={`${current?.amount} ${transaction?.paidCurrency?.currency}`}
            />
          ),
        })
      })
    }

    switch (transaction?.status) {
      case PaymentStatus.EXPIRED:
        if (transaction?.expiryDate <= Date.now()) {
          updatedTimelineRows.push({
            date: new Date(transaction?.expiryDate),
            variant: 'error',
            Title: <PaymentTimelineRowInfo generalInfo={t('paymentExpired')} />,
          })
        }
        if (transaction?.latePayment) {
          updatedTimelineRows.push({
            variant: 'error',
            Title: (
              <PaymentTimelineRowInfo
                generalInfo={t('latePaymentDescription', {
                  paidAmount: transaction?.paidCurrency?.actual,
                  paidCurrency: transaction?.paidCurrency?.currency,
                })}
              />
            ),
          })
        }
        break

      case PaymentStatus.UNDERPAID:
        transaction?.transactions?.forEach(current => {
          updatedTimelineRows.push({
            variant: 'info',
            date: new Date(current?.dateConfirmed),
            Title: (
              <PaymentTimelineRowInfo
                generalInfo={t('paymentOfTimeline', {
                  action: isPaymentIn ? t('payments.received') : t('payments.sent'),
                })}
                testid="timeline-action-of-value"
                boldInfo={`${current?.amount} ${transaction?.paidCurrency?.currency}`}
              />
            ),
          })
        })

        updatedTimelineRows.push({
          variant: 'warning',
          Title: isPaymentIn ? (
            <PaymentTimelineRowInfo
              generalInfo={t('amountDue')}
              testid="timeline-amount-due-value"
              boldInfo={`${amountDue} ${transaction?.paidCurrency?.currency}${
                canMarkPaymentComplete
                  ? ` (${calculatePercentageDiff(
                      transaction?.paidCurrency?.amount,
                      transaction?.paidCurrency?.actual,
                      'remaining'
                    )} ${toLower(t('owing'))})`
                  : ''
              }`}
            />
          ) : (
            <PaymentTimelineRowInfo generalInfo={t('paymentIsProcessing')} />
          ),
        })
        break

      case PaymentStatus.OVERPAID:
        transaction?.transactions?.forEach(current => {
          updatedTimelineRows.push({
            variant: 'info',
            date: new Date(current?.dateConfirmed),
            Title: (
              <PaymentTimelineRowInfo
                generalInfo={t('paymentOfTimeline', {
                  action: isPaymentIn ? t('payments.received') : t('payments.sent'),
                })}
                testid="timeline-action-of-value"
                boldInfo={`${current?.amount} ${transaction?.paidCurrency?.currency}`}
              />
            ),
          })
        })
        break
      case PaymentStatus.COMPLETE:
        const latestTransaction = transaction?.transactions?.reduce((latest, current) => {
          return new Date(current?.dateConfirmed) > new Date(latest?.dateConfirmed)
            ? current
            : latest
        }, transaction?.transactions?.[0])

        updatedTimelineRows.push({
          variant: 'success',
          date: new Date(latestTransaction?.dateConfirmed),
          Title: (
            <PaymentTimelineRowInfo
              generalInfo={t('paymentOfTimeline', {
                action: isPaymentIn ? t('payments.received') : t('payments.sent'),
              })}
              testid="timeline-action-of-value"
              boldInfo={`${transaction?.paidCurrency?.actual} ${transaction?.paidCurrency?.currency}${
                canMarkPaymentComplete
                  ? ` (${calculatePercentageDiff(
                      transaction?.paidCurrency?.amount,
                      transaction?.paidCurrency?.actual,
                      'paid'
                    )} ${toLower(t('paid'))})`
                  : ''
              }`}
            />
          ),
        })
        break

      default:
        break
    }

    if (!!transaction?.hasEnhancedChecks) {
      updatedTimelineRows.push({
        variant: 'warning',
        Title: <PaymentTimelineRowInfo generalInfo={t('payments.statusOnHoldTimeline')} />,
      })
    }

    if (updatedTimelineRows?.length > values?.length) {
      updatedTimelineRows = updatedTimelineRows.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
      handlers.setState(updatedTimelineRows)
    }
  }, [transaction])

  return values
}
